<template>
  <div
    class="cart-container bg-white shadow-lg w-32 rounded-4 shadow-sm d-flex flex-column"
    :class="{ expanded: isExpanded }"
  >
    <!-- Header (only visible when expanded) -->
    <div
      v-if="isExpanded"
      class="d-flex position-relative pt-4-5 ps-4-5 pe-4-5 align-items-center"
    >
      <h5 class="fw-bold position-relative text-darkblue z-1">
        JOUW BESTELLING
      </h5>
      <img
        alt="dec"
        src="https://sweetdeliver.nl/wp-content/uploads/2024/09/sweet-dec.svg"
        class="position-absolute end-0 z-0"
        style="width: 40px; top: 35px; right: 130px !important"
      />
    </div>

    <!-- Empty cart message -->
    <p class="ps-4-5 mt-2" v-if="orderItems.length === 0 && isExpanded">
      Op dit moment heb je nog geen producten in je winkelwagen.
    </p>

    <!-- Cart items -->
    <div
      v-else-if="isExpanded"
      class="overflow-auto pe-4-5 ps-4-5"
      style="max-height: 380px"
    >
      <ul>
        <li
          v-for="(item, index) in orderItems"
          :key="item.uniqueCartId"
          :class="`d-flex position-relative flex-column mt-4 ${
            index !== orderItems.length - 1 ? 'border-bottom pb-3' : ''
          } ${
            props.editingItem &&
            props.editingItem.uniqueCartId === item.uniqueCartId
              ? 'opacity-50'
              : ''
          }`"
        >
          <!-- Basic item info (image, box name, receiver name) -->
          <div class="d-flex align-items-center" v-if="item.pickedBox">
            <img
              :src="
                item.pickedBox?.acf?.boxpicker_afbeelding?.url ||
                item.pickedBox?.images?.[0]?.src ||
                item.pickedBox?.image
              "
              alt="Product Image"
              width="100"
              class="cart-item-image rounded-pill"
            />
            <div class="ms-3 w-50">
              <p class="fs-14 pb-0">{{ item.pickedBox?.name }}</p>
              <p class="fs-14 text-muted pb-0">{{ item.receiver?.name }}</p>
            </div>
          </div>

          <!-- Flavors and item subtotal -->
          <div class="d-flex align-items-center mt-3 flex-wrap gap-2" v-if="item.pickedBox">
            <div
              class="bg-blue me-2 d-flex rounded-pill align-items-center justify-content-center"
              v-for="flavor in item.flavours"
              :key="flavor.id"
            >
              <img :src="flavor.image" alt="Flavour Image" width="34" />
            </div>
            <!-- Subtotal = box price * item.quantity -->
            <p class="ms-auto fs-14">
              €{{ (parseFloat(item.pickedBox.price) * (item.quantity || 1)).toFixed(2) }}
            </p>
          </div>

          <!-- Quantity controls & toggle "Alle informatie" -->
          <div class="d-flex align-items-center mt-3 flex-wrap gap-2">
            <!-- Decrease Quantity -->
            <button
              class="sd-quantity-action min d-flex align-items-center justify-content-center rounded-pill me-2"
              @click="decreaseQuantity(item)"
              aria-label="Decrease quantity"
            >
              -
            </button>
            <!-- Current Quantity -->
            <div class="sd-quantity-indicator">{{ item.quantity || 1 }}</div>
            <!-- Increase Quantity -->
            <button
              class="sd-quantity-action plus d-flex align-items-center justify-content-center rounded-pill ms-2"
              @click="increaseQuantity(item)"
              aria-label="Increase quantity"
            >
              +
            </button>
            <!-- Toggle Additional Info -->
            <div
              class="ms-auto"
              role="button"
              @click="toggleAllInfo(item.uniqueCartId)"
            >
              <p class="fs-14 text-primary" style="text-decoration: underline;">
                Alle informatie
              </p>
            </div>
          </div>

          <!-- Additional Information -->
          <div
            v-if="showAllInfoMap[item.uniqueCartId]"
            class="mt-3 d-flex flex-column align-items-start"
          >
            <p class="fs-15 pb-0">{{ item.receiver?.name }}</p>
            <p class="fs-15 pb-0">
              {{ item.receiver?.city }}, {{ item.receiver?.zipcode }}
            </p>
            <p class="fs-15 pb-0">
              {{ item.receiver?.street }} {{ item.receiver?.number }}
            </p>
            <!-- If there's a selected card -->
            <p v-if="item.selectedCard" class="fs-15 pb-0">
              Gekozen kaart: {{ item.selectedCard.name }}
            </p>
            <p v-if="item.selectedCard" class="fs-15 pb-0">
              {{ item.card?.text }}
            </p>
          </div>

          <!-- Edit & Remove Buttons -->
          <div
            class="d-flex align-items-center position-absolute top-0 end-0 sd-btn-flex-col"
          >
            <button
              @click="$emit('edit-item', item)"
              class="icon-btn rounded-pill bg-transparent border-0 me-1"
              style="margin: 0 !important;"
              aria-label="Edit item"
            >
              <!-- Edit SVG -->
              <svg
                style="width: 20px; height: 20px;"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#152E88"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"/>
                <path d="M13.5 6.5l4 4"/>
              </svg>
            </button>
            <button
              @click="removeItem(item.uniqueCartId)"
              class="icon-btn rounded-pill bg-transparent border-0"
              aria-label="Remove item"
            >
              <!-- Remove SVG -->
              <svg
                style="width: 18px; height: 18px;"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="none"
                  stroke="#FF4A4A"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 6v-.8c0-1.13 0-1.69-.22-2.11 -.2-.38-.5-.69-.88-.88 -.43-.22-.99-.22-2.11-.22h-1.6c-1.13 0-1.69 0-2.11.21 -.38.19-.69.49-.88.87 -.22.42-.22.98-.22 2.1v.8m2 5.5v5m4-5v5m-11-10.5h18m-2 0v11.2c0 1.68 0 2.52-.33 3.16 -.29.56-.75 1.02-1.32 1.31 -.65.32-1.49.32-3.17.32h-4.4c-1.69 0-2.53 0-3.17-.33 -.57-.29-1.03-.75-1.32-1.32 -.33-.65-.33-1.49-.33-3.17V5.94"
                />
              </svg>
            </button>
          </div>
        </li>
      </ul>
    </div>

    <!-- Bottom section: summary bar + toggle button -->
    <div
      class="d-flex align-items-center ps-4 mt-auto pe-4 py-3"
      @click="toggleExpand"
      role="button"
    >
      <!-- Cart summary (always shown, but clickable) -->
      <div
        role="button"
        class="d-flex align-items-center justify-content-between py-2 fw-medium fs-15"
      >
        <div class="position-relative">
          <svg
            data-icon="cart-1"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            class="fkcart-icon-checkout"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2.71411C2 2.31972 2.31972 2 2.71411 2H3.34019C4.37842 2 4.97454 2.67566 5.31984 3.34917C5.55645 3.8107 5.72685 4.37375 5.86764 4.86133H20.5709C21.5186 4.86133 22.2035 5.7674 21.945 6.67914L19.809 14.2123C19.4606 15.4413 18.3384 16.2896 17.0609 16.2896H9.80665C8.51866 16.2896 7.39 15.4276 7.05095 14.185L6.13344 10.8225C6.12779 10.8073 6.12262 10.7917 6.11795 10.7758L4.64782 5.78023C4.59738 5.61449 4.55096 5.45386 4.50614 5.29878C4.36354 4.80529 4.23716 4.36794 4.04891 4.00075C3.82131 3.55681 3.61232 3.42822 3.34019 3.42822H2.71411C2.31972 3.42822 2 3.1085 2 2.71411ZM7.49529 10.3874L8.4288 13.8091C8.59832 14.4304 9.16266 14.8613 9.80665 14.8613H17.0609C17.6997 14.8613 18.2608 14.4372 18.435 13.8227L20.5709 6.28955H6.28975L7.49529 10.3874ZM12.0017 19.8577C12.0017 21.0408 11.0426 22 9.85941 22C8.67623 22 7.71708 21.0408 7.71708 19.8577C7.71708 18.6745 8.67623 17.7153 9.85941 17.7153C11.0426 17.7153 12.0017 18.6745 12.0017 19.8577ZM10.5735 19.8577C10.5735 19.4633 10.2538 19.1436 9.85941 19.1436C9.46502 19.1436 9.1453 19.4633 9.1453 19.8577C9.1453 20.2521 9.46502 20.5718 9.85941 20.5718C10.2538 20.5718 10.5735 20.2521 10.5735 19.8577ZM19.1429 19.8577C19.1429 21.0408 18.1837 22 17.0005 22C15.8173 22 14.8582 21.0408 14.8582 19.8577C14.8582 18.6745 15.8173 17.7153 17.0005 17.7153C18.1837 17.7153 19.1429 18.6745 19.1429 19.8577ZM17.7146 19.8577C17.7146 19.4633 17.3949 19.1436 17.0005 19.1436C16.6061 19.1436 16.2864 19.4633 16.2864 19.8577C16.2864 20.2521 16.6061 20.5718 17.0005 20.5718C17.3949 20.5718 17.7146 20.2521 17.7146 19.8577Z"
              fill="currentColor"
            ></path>
          </svg>
          <span
            class="sd-badge d-flex align-items-center justify-content-center text-white rounded-pill position-absolute top-0 end-0"
            >{{ orderItems.length }}</span
          >
        </div>
        <!-- Show total amount -->
        <p class="ms-2 mt-1">
          €{{ totalAmount?.toFixed(2).toString().replace('.', ',') }}
        </p>
      </div>

      <!-- Checkout button -->
      <div class="d-flex ms-auto align-items-center justify-content-between checkout-button">
        <button
          @click.prevent.stop="$emit('checkout')"
          class="bg-red fs-15 text-light py-2-5 px-4 border-0 rounded-pill ms-auto"
        >
          Afrekenen
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineProps } from 'vue';
import { useOrderStore } from '@/stores/orderStore';

const props = defineProps({
  editingItem: Object, // same as your desktop cart
});

// Access the order store
const orderStore = useOrderStore();

// Computed: cart items & total amount
const orderItems = computed(() => orderStore.items);
const totalAmount = computed(() => orderStore.totalAmount);

// Whether the mobile cart is expanded or collapsed
const isExpanded = ref(false);
const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

// Map of booleans to show/hide extra info for each item
const showAllInfoMap = ref({});

/**
 * Toggle additional info (receiver details, card, etc.) for a given item.
 */
const toggleAllInfo = (uniqueCartId) => {
  showAllInfoMap.value[uniqueCartId] = !showAllInfoMap.value[uniqueCartId];
};

/**
 * Increase item quantity.
 */
const increaseQuantity = (item) => {
  const newQuantity = (item.quantity || 1) + 1;
  orderStore.updateItemQuantity(item.uniqueCartId, newQuantity);
};

/**
 * Decrease item quantity (cannot go below 1).
 */
const decreaseQuantity = (item) => {
  if ((item.quantity || 1) > 1) {
    const newQuantity = (item.quantity || 1) - 1;
    orderStore.updateItemQuantity(item.uniqueCartId, newQuantity);
  }
};

/**
 * Remove an item by uniqueCartId.
 */
const removeItem = (uniqueCartId) => {
  orderStore.removeItem(uniqueCartId);
};
</script>

<style scoped>
.cart-container.expanded {
  max-height: 80vh;
}

.rounded-4 {
  border-radius: 15px !important;
}

.w-32 {
  width: 36%;
  min-width: 36%;
}

ul {
  list-style: none;
  padding: 0px;
}

.bg-blue {
  background-color: #f7f7f7;
  width: 44px;
  height: 44px;
}

.cart-item-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
}

.fs-14 {
  font-size: 15px;
}

/* Quantity Controls */
.d-flex.align-items-center.mt-3 button {
  width: 30px;
  height: 30px;
  padding: 0;
}
.sd-quantity-action.min,
.sd-quantity-action.plus {
  border: 1px solid #dddddd;
  background-color: white;
  font-size: 18px;
  line-height: 1;
}
.sd-quantity-indicator {
  width: 30px;
  text-align: center;
  font-size: 14px;
  margin-right: 8px;
}

.icon-btn:hover {
  background-color: #f1f1f1 !important;
}

.sd-badge {
  width: 16px;
  height: 16px;
  font-size: 12px;
  margin-top: -5px;
  margin-right: -5px;
  background-color: #ff4a4a;
  text-align: center;
}

.checkout-button button {
  padding-block: 15px;
}

@media (max-width: 1000px) {
  .cart-container {
    position: fixed;
    bottom: 20px;
    left: 0px;
    width: 95vw;
    max-width: 95vw;
    margin-left: 0px !important;
    z-index: 2;
    left: 2.5vw;
  }
}
</style>
